import { FieldArray, Form, Formik } from 'formik';
import React from 'react';
import {
  AreaField,
  Button,
  FlexRow,
  Grid,
  ISelectOption,
  ISpruceFieldProps,
  IWrappedField,
  NumberField,
  RadioList,
  SelectField,
  SpruceField,
  TextField,
} from 'ui-toolkit/index';
import {
  eTaxCertificationClassCodes,
  TaxCertificationClassCodesLabels,
} from 'ui-toolkit/taxCertifications/eTaxCertificationClassCodes';
import { Jurisdictions } from './Jurisdictions';
import { TaxCertificationDelinquencies } from './TaxCertificationDelinquencies';
import { ITaxCertificationData } from 'ui-toolkit/taxCertifications/ITaxCertification';
import { createDefaultTaxCertificationDelinquenciesData } from 'ui-toolkit/taxCertifications/ITaxCertificationDelinquencies';
import { StateCountySelect } from './StateCountySelect';
import { ICountiesState } from '../../../store/counties';
import { taxCertificationSchema } from 'store/order/taxCertifications/taxCertificationSchema';
import { eUSAState } from '../../../ui-toolkit/enums/states';

export interface ITaxCertificationFormProps {
  onSave: (x: ITaxCertificationData) => Promise<void>;
  onCancel: () => void;
  existingTaxCertData: ITaxCertificationData;
  countiesState: ICountiesState;
  onStateSelect: (state: eUSAState | null) => void;
}

export const TaxCertificationForm: React.FC<ITaxCertificationFormProps> = ({
  onSave,
  onCancel,
  existingTaxCertData,
  countiesState: { counties, countiesFetchStatus },
  onStateSelect,
}) => {
  const TaxCertificationClassCodesOptions: ISelectOption[] = Object.values(eTaxCertificationClassCodes).map((code) => ({
    label: TaxCertificationClassCodesLabels[code],
    value: code,
  }));

  const onRadioChange = (field: IWrappedField, onTrue: () => void, onFalse: () => void) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      field.onChange(event.target.value);
      if (event.target.value === 'true') {
        onTrue();
      }
      if (event.target.value === 'false') {
        onFalse();
      }
    };
  };

  return (
    <Formik
      validationSchema={taxCertificationSchema}
      initialValues={existingTaxCertData}
      enableReinitialize={true}
      onSubmit={async (values) => await onSave(values)}
    >
      {({ values }) => (
        <Form>
          <Grid>
            <Grid columnCount={4}>
              <TextField name="parcelNumber" label={`Parcel Number`} placeholder={'Type Here'} />
              <SelectField
                name={`classCode`}
                data-test-id={`classCode`}
                options={TaxCertificationClassCodesOptions}
                label={`Class Code`}
                placeholder={`Select`}
              />
            </Grid>

            <Grid columnCount={2}>
              <TextField
                name={`propertyOwner`}
                data-test-id={`propertyOwner`}
                label="Property Owner"
                placeholder="Type here"
              />
            </Grid>

            <Grid columnCount={5}>
              <TextField
                name={`propertyStreetAddress`}
                data-test-id={`propertyStreetAddress`}
                label="Property Street Address"
                placeholder="Type here"
              />
              <TextField
                name={`propertyFloorSuite`}
                data-test-id={`propertyFloorSuite`}
                label="Floor/Suite"
                placeholder="Type here"
              />
              <TextField name={`propertyCity`} data-test-id={`propertyCity`} label="City" placeholder="Type here" />
              <StateCountySelect
                stateName={`propertyState`}
                countyName={`propertyCounty`}
                onStateSelect={onStateSelect}
                counties={
                  countiesFetchStatus.fetched && countiesFetchStatus.success && values.propertyState
                    ? counties[values.propertyState]
                    : []
                }
              />
              <NumberField
                name={`propertyZipCode`}
                data-test-id={`propertyZipCode`}
                label="Zip Code"
                placeholder="Type here"
              />
            </Grid>
            <Jurisdictions baseName={`jurisdictions`} />
            <AreaField name={'discounts'} placeholder={'Type here'} label="Discounts" />
            <AreaField name={'notes'} placeholder={'Type here'} label="Additional Notes" />
            <Grid columnCount={3}>
              <NumberField
                name={'taxableValueCents'}
                label="Taxable value"
                placeholder="Type here"
                mode={'currency'}
                valueIsCents
              />
              <NumberField
                name={'improvementValueCents'}
                label="Improvement Value"
                placeholder="Type here"
                mode={'currency'}
                valueIsCents
              />
              <NumberField
                name={'exemptionCents'}
                label="Exemption"
                placeholder="Type here"
                mode={'currency'}
                valueIsCents
              />
            </Grid>
            <Grid columnCount={2}>
              <NumberField
                name={'landValueCents'}
                label="Land Value"
                placeholder="Type here"
                mode={'currency'}
                valueIsCents
              />
              <NumberField
                name={'marketValueCents'}
                label="Market Value"
                placeholder="Type here"
                mode={'currency'}
                valueIsCents
              />
            </Grid>
            <Grid columnCount={3}>
              <NumberField name={`assessedYear`} label="Assessed Year" placeholder="Type here" />
              <TextField
                name={`totalAssessedValue`}
                data-test-id={`totalAssessedValue`}
                label="Total Assessed Value"
                placeholder="Type here"
              />
              <TextField
                name={`assessedMarketValue`}
                data-test-id={`assessedMarketValue`}
                label="Assessed Market Value"
                placeholder="Type here"
              />
            </Grid>

            <FieldArray name="taxCertificationDelinquencies">
              {({ push }) => (
                <SpruceField name={`hasPriorDelinquencies`}>
                  {({ field, formik }: ISpruceFieldProps) => (
                    <>
                      <Grid columnCount={3}>
                        <RadioList
                          {...field}
                          orientation={'horizontal'}
                          label="Are there any prior year delinquencies?"
                          options={[
                            { label: 'Yes', value: `true` },
                            { label: 'No', value: `false` },
                          ]}
                          onChange={onRadioChange(
                            field,
                            () => push(createDefaultTaxCertificationDelinquenciesData()),
                            () => formik.setFieldValue('taxCertificationDelinquencies', [])
                          )}
                          value={{ true: `true`, false: `false` }[field.value]}
                        />
                      </Grid>
                      {field.value && field.value !== `false` && (
                        <TaxCertificationDelinquencies baseName={`taxCertificationDelinquencies`} />
                      )}
                    </>
                  )}
                </SpruceField>
              )}
            </FieldArray>

            <SpruceField name={`hasExemptions`}>
              {({ field, formik }: ISpruceFieldProps) => (
                <>
                  <Grid columnCount={3}>
                    <RadioList
                      {...field}
                      orientation={'horizontal'}
                      label="Are there any exemptions?"
                      options={[
                        { label: 'Yes', value: `true` },
                        { label: 'No', value: `false` },
                      ]}
                      onChange={onRadioChange(
                        field,
                        () => {},
                        () => formik.setFieldValue('exemptionsNotes', ``)
                      )}
                      value={{ true: `true`, false: `false` }[field.value]}
                    />
                  </Grid>
                  {field.value && field.value !== `false` && (
                    <AreaField name={'exemptionsNotes'} placeholder={'Type here'} />
                  )}
                </>
              )}
            </SpruceField>

            <SpruceField name={`hasAllParcels`}>
              {({ field, formik }: ISpruceFieldProps) => (
                <>
                  <Grid columnCount={3}>
                    <RadioList
                      {...field}
                      orientation={'horizontal'}
                      label="Are all parcels associated searched?"
                      options={[
                        { label: 'Yes', value: `true` },
                        { label: 'No', value: `false` },
                      ]}
                      onChange={onRadioChange(
                        field,
                        () => {},
                        () => formik.setFieldValue('allParcelsNotes', ``)
                      )}
                      value={{ true: `true`, false: `false` }[field.value]}
                    />
                  </Grid>
                  {field.value && field.value !== `false` && (
                    <AreaField name={'allParcelsNotes'} placeholder={'Type here'} />
                  )}
                </>
              )}
            </SpruceField>

            <SpruceField name={`hasOtherTaxingAuthorities`}>
              {({ field, formik }: ISpruceFieldProps) => (
                <>
                  <Grid columnCount={3}>
                    <RadioList
                      {...field}
                      orientation={'horizontal'}
                      label="Are there any other taxing authorities?"
                      options={[
                        { label: 'Yes', value: `true` },
                        { label: 'No', value: `false` },
                      ]}
                      onChange={onRadioChange(
                        field,
                        () => {},
                        () => formik.setFieldValue('otherTaxingAuthoritiesNotes', ``)
                      )}
                      value={{ true: `true`, false: `false` }[field.value]}
                    />
                  </Grid>
                  {field.value && field.value !== `false` && (
                    <AreaField name={'otherTaxingAuthoritiesNotes'} placeholder={'Type here'} />
                  )}
                </>
              )}
            </SpruceField>
            <FlexRow justifyContent={'end'}>
              <Button intent={'secondary'} onClick={onCancel} type={'reset'}>
                Cancel
              </Button>
              <Button intent={'primary'} type="submit">
                Save and Exit
              </Button>
            </FlexRow>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
